import React, { useEffect, useState } from 'react';
import { MdArrowForward } from 'react-icons/md';
import ProtectedLink from '../ProtectedLink';
import { categories as fallbackCategories } from '../../data/mockData';
import { listBackendCategories, categoryToFrontend } from '../../services/categoriesApi';
import { useAuthStore } from '../../store/authStore';
import type { Category } from '../../types';
import image61 from '../../assets/image61.png';
import image62 from '../../assets/image62.png';
import image63 from '../../assets/image63.png';
import image64 from '../../assets/image64.png';
import image65 from '../../assets/image65.png';
import image66 from '../../assets/image66.png';
import '../../styles/home/BrowseCategorySection.css';

const categoryImages = [image62, image61, image64, image63, image65, image66];

const BrowseCategoryCard: React.FC<{
  name: string;
  slug: string;
  image: string;
  description: string;
}> = ({ name, slug, image, description }) => {
  return (
    <div className="cat-card">
      <div className="cat-card__body">
        <div className="cat-card__image-wrap">
          <img
            src={image}
            alt={name}
            className="cat-card__image"
            loading="lazy"
          />
        </div>
        <div className="cat-card__info">
          <h3 className="cat-card__title">{name}</h3>
          <p className="cat-card__desc">{description}</p>
          <ProtectedLink to={`/products?category=${slug}`} className="cat-card__link">
            Browse Category <MdArrowForward />
          </ProtectedLink>
        </div>
      </div>
      <div className="cat-card__accent" />
    </div>
  );
};

const BrowseCategorySection: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // The categories API requires auth; on the public home page we fall back to
  // the static teaser when logged out, and show live categories once signed in.
  const [categories, setCategories] = useState<Category[]>(fallbackCategories);

  useEffect(() => {
    if (!isAuthenticated) {
      setCategories(fallbackCategories);
      return;
    }
    let cancelled = false;
    listBackendCategories()
      .then((list) => {
        if (!cancelled && list.length) setCategories(list.map(categoryToFrontend));
      })
      .catch(() => {
        /* keep fallback on error */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const displayCategories = categories.slice(0, 6);

  return (
    <section className="browse-category">
      <div className="browse-category__container">
        {/* Header row */}
        <div className="browse-category__header">
          <div>
            <span className="browse-category__tag">PRODUCT RANGE</span>
            <h2 className="browse-category__heading">Browse by Category</h2>
          </div>
          <ProtectedLink to="/categories" className="browse-category__view-all">
            VIEW ALL CATEGORIES <MdArrowForward />
          </ProtectedLink>
        </div>

        {/* Grid */}
        <div className="browse-category__grid">
          {displayCategories.map((category, index) => (
            <BrowseCategoryCard
              key={category.id}
              name={category.name}
              slug={category.slug}
              image={categoryImages[index] || image61}
              description={category.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrowseCategorySection;
